// Efeitos sonoros procedurais usando Web Audio API
// Otimizado com preloading e buffer caching para evitar flickering

let audioContext: AudioContext | null = null;
let isWarmedUp = false;

// Cache de buffers de ruído pré-calculados para evitar criação em runtime
const noiseBufferCache: { [key: string]: AudioBuffer } = {};

/**
 * Obtém ou cria o AudioContext com tratamento robusto
 */
function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;

  if (!audioContext || audioContext.state === "closed") {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (e) {
      console.warn("[ProceduralSfx] Failed to create AudioContext:", e);
      return null;
    }
  }

  // Resume se suspenso - não bloqueia, retorna o contexto mesmo assim
  // O som será agendado e tocará assim que o contexto for resumido
  if (audioContext.state === "suspended") {
    audioContext.resume().catch(() => {});
  }

  return audioContext;
}

/**
 * Retorna o AudioContext global para uso compartilhado
 */
export function getSharedAudioContext(): AudioContext | null {
  return getAudioContext();
}

/**
 * Pré-carrega buffers de ruído para evitar criação em runtime
 */
function getNoiseBuffer(ctx: AudioContext, duration: number, type: "white" | "crackle" = "white"): AudioBuffer {
  const key = `${type}-${duration}`;

  if (noiseBufferCache[key]) {
    return noiseBufferCache[key];
  }

  const bufferSize = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  if (type === "white") {
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
  } else if (type === "crackle") {
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() > 0.95 ? (Math.random() * 2 - 1) : 0;
    }
  }

  noiseBufferCache[key] = buffer;
  return buffer;
}

/**
 * Aquece o sistema de áudio - chame uma vez na inicialização
 */
export function warmupAudio(): void {
  if (isWarmedUp) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  // Pré-criar buffers de ruído comuns
  getNoiseBuffer(ctx, 0.1, "white");
  getNoiseBuffer(ctx, 0.2, "white");
  getNoiseBuffer(ctx, 0.3, "white");
  getNoiseBuffer(ctx, 0.4, "white");
  getNoiseBuffer(ctx, 0.2, "crackle");
  getNoiseBuffer(ctx, 0.3, "crackle");

  // Tocar som silencioso para desbloquear AudioContext em mobile
  const silentOsc = ctx.createOscillator();
  const silentGain = ctx.createGain();
  silentGain.gain.value = 0.001;
  silentOsc.connect(silentGain);
  silentGain.connect(ctx.destination);
  silentOsc.start();
  silentOsc.stop(ctx.currentTime + 0.01);

  isWarmedUp = true;
  console.log("[ProceduralSfx] Audio warmed up");
}

/**
 * Verifica se o áudio está pronto para tocar
 */
export function isAudioReady(): boolean {
  const ctx = getAudioContext();
  return ctx !== null && ctx.state === "running";
}

// Som de tiro - laser futurístico melhorado (estilo sci-fi)
export function playShootSound(volume: number = 0.3): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  // Oscilador principal - sweep descendente suave
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.type = "sine";
  osc.frequency.setValueAtTime(1200, now);
  osc.frequency.exponentialRampToValueAtTime(300, now + 0.08);
  
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(3000, now);
  filter.frequency.exponentialRampToValueAtTime(800, now + 0.08);
  filter.Q.value = 2;

  // Envelope suave anti-clique
  gain.gain.setValueAtTime(0.001, now);
  gain.gain.exponentialRampToValueAtTime(volume * 0.4, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);

  osc.start(now);
  osc.stop(now + 0.12);

  // Harmônico agudo para brilho
  const harmonic = ctx.createOscillator();
  const harmGain = ctx.createGain();
  
  harmonic.type = "triangle";
  harmonic.frequency.setValueAtTime(2400, now);
  harmonic.frequency.exponentialRampToValueAtTime(600, now + 0.06);
  
  harmGain.gain.setValueAtTime(0.001, now);
  harmGain.gain.exponentialRampToValueAtTime(volume * 0.15, now + 0.003);
  harmGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
  
  harmonic.connect(harmGain);
  harmGain.connect(ctx.destination);
  
  harmonic.start(now);
  harmonic.stop(now + 0.08);
}

// Som de impacto/hit - mais satisfatório e musical
export function playHitSound(volume: number = 0.5): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  // Thump grave - impacto profundo
  const thump = ctx.createOscillator();
  const thumpGain = ctx.createGain();

  thump.type = "sine";
  thump.frequency.setValueAtTime(180, now);
  thump.frequency.exponentialRampToValueAtTime(60, now + 0.06);

  thumpGain.gain.setValueAtTime(0.001, now);
  thumpGain.gain.exponentialRampToValueAtTime(volume * 0.5, now + 0.003);
  thumpGain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

  thump.connect(thumpGain);
  thumpGain.connect(ctx.destination);

  thump.start(now);
  thump.stop(now + 0.12);

  // "Punch" médio para presença
  const punch = ctx.createOscillator();
  const punchGain = ctx.createGain();
  const punchFilter = ctx.createBiquadFilter();

  punch.type = "triangle";
  punch.frequency.setValueAtTime(400, now);
  punch.frequency.exponentialRampToValueAtTime(150, now + 0.04);
  
  punchFilter.type = "bandpass";
  punchFilter.frequency.value = 300;
  punchFilter.Q.value = 1;

  punchGain.gain.setValueAtTime(0.001, now);
  punchGain.gain.exponentialRampToValueAtTime(volume * 0.3, now + 0.002);
  punchGain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

  punch.connect(punchFilter);
  punchFilter.connect(punchGain);
  punchGain.connect(ctx.destination);

  punch.start(now);
  punch.stop(now + 0.07);
}

// Som de explosão - épico e cinematográfico
export function playExplosionSound(volume: number = 0.6): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  // Boom grave profundo
  const boom = ctx.createOscillator();
  const boomGain = ctx.createGain();
  const boomFilter = ctx.createBiquadFilter();

  boom.type = "sine";
  boom.frequency.setValueAtTime(80, now);
  boom.frequency.exponentialRampToValueAtTime(25, now + 0.25);
  
  boomFilter.type = "lowpass";
  boomFilter.frequency.value = 150;

  boomGain.gain.setValueAtTime(0.001, now);
  boomGain.gain.exponentialRampToValueAtTime(volume * 0.7, now + 0.01);
  boomGain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

  boom.connect(boomFilter);
  boomFilter.connect(boomGain);
  boomGain.connect(ctx.destination);

  boom.start(now);
  boom.stop(now + 0.4);

  // Camada média - corpo da explosão
  const mid = ctx.createOscillator();
  const midGain = ctx.createGain();
  
  mid.type = "triangle";
  mid.frequency.setValueAtTime(200, now);
  mid.frequency.exponentialRampToValueAtTime(50, now + 0.15);
  
  midGain.gain.setValueAtTime(0.001, now);
  midGain.gain.exponentialRampToValueAtTime(volume * 0.35, now + 0.008);
  midGain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  
  mid.connect(midGain);
  midGain.connect(ctx.destination);
  
  mid.start(now);
  mid.stop(now + 0.25);

  // Ruído de detritos (suave)
  const noise = ctx.createBufferSource();
  const noiseGain = ctx.createGain();
  const noiseFilter = ctx.createBiquadFilter();

  noise.buffer = getNoiseBuffer(ctx, 0.3, "white");
  noiseFilter.type = "bandpass";
  noiseFilter.frequency.setValueAtTime(1500, now);
  noiseFilter.frequency.exponentialRampToValueAtTime(300, now + 0.2);
  noiseFilter.Q.value = 0.7;

  noiseGain.gain.setValueAtTime(0.001, now + 0.01);
  noiseGain.gain.exponentialRampToValueAtTime(volume * 0.2, now + 0.02);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);

  noise.start(now);
  noise.stop(now + 0.3);
}

// Som de sucesso/vitória - mais celebratório
export function playSuccessSound(volume: number = 0.5): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  // Arpeggio ascendente
  const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6

  notes.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "sine";
    osc.frequency.value = freq;

    const startTime = now + i * 0.1;
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(volume * 0.4, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(startTime);
    osc.stop(startTime + 0.35);
  });

  // Sparkle (usando buffer cacheado)
  const sparkle = ctx.createBufferSource();
  const sparkleGain = ctx.createGain();
  const sparkleFilter = ctx.createBiquadFilter();

  sparkle.buffer = getNoiseBuffer(ctx, 0.3, "crackle");
  sparkleFilter.type = "highpass";
  sparkleFilter.frequency.value = 6000;

  sparkleGain.gain.setValueAtTime(volume * 0.15, now);
  sparkleGain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

  sparkle.connect(sparkleFilter);
  sparkleFilter.connect(sparkleGain);
  sparkleGain.connect(ctx.destination);

  sparkle.start(now);
  sparkle.stop(now + 0.4);
}

// Som de tiro carregado - mais poderoso
export function playChargedShootSound(chargeLevel: number, volume: number = 0.5): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  const now = ctx.currentTime;
  const level = Math.max(0, Math.min(1, chargeLevel));

  // Mais intenso quanto maior o charge
  const duration = 0.1 + level * 0.2;
  const startFreq = 600 + level * 600;
  const endFreq = 100 + level * 100;

  // Oscilador principal
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();

  osc.type = level > 0.5 ? "sawtooth" : "square";
  osc.frequency.setValueAtTime(startFreq, now);
  osc.frequency.exponentialRampToValueAtTime(endFreq, now + duration);

  gain.gain.setValueAtTime(volume * (0.3 + level * 0.4), now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);

  osc.start(now);
  osc.stop(now + duration + 0.05);

  // Sub bass para charge alto
  if (level > 0.5) {
    const sub = ctx.createOscillator();
    const subGain = ctx.createGain();

    sub.type = "sine";
    sub.frequency.setValueAtTime(80, now);
    sub.frequency.exponentialRampToValueAtTime(40, now + duration);

    subGain.gain.setValueAtTime(volume * level * 0.4, now);
    subGain.gain.exponentialRampToValueAtTime(0.001, now + duration);

    sub.connect(subGain);
    subGain.connect(ctx.destination);

    sub.start(now);
    sub.stop(now + duration + 0.05);
  }

  // Noise burst (usando buffer cacheado mais próximo)
  const noise = ctx.createBufferSource();
  const noiseGain = ctx.createGain();

  // Usar buffer de 0.3s (mais próximo da duração típica)
  noise.buffer = getNoiseBuffer(ctx, 0.3, "white");
  noiseGain.gain.setValueAtTime(volume * level * 0.2, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.8);

  noise.connect(noiseGain);
  noiseGain.connect(ctx.destination);

  noise.start(now);
  noise.stop(now + duration);
}
